import { getDefaultColorForPath, getFullVariableDefaults } from './themeDefaults';
import {
  THEME_FIELD_DEFAULTS,
  THEME_FIELD_REGISTRY,
  THEME_FIELD_TYPES,
} from './themeFieldRegistry';
import {
  fontStyleOptions as FONT_STYLE_OPTIONS,
  fontSizeOptions as FONT_SIZE_OPTIONS,
} from './themeFieldRegistry';
import { binFieldsIntoEyebrows, detectElementVariant, isTextPaintTail } from './eyebrowBinning';
import { THEME_PROPERTY_REGISTRY } from '@repo/checkout/theming/themePropertyRegistry';
import resolveBadge from '@repo/checkout/lib/resolveBadge';
import isPayingSeller from '@repo/checkout/lib/isPayingSeller';

const PROPERTY_TYPE_BY_PATH = Object.fromEntries(
  THEME_PROPERTY_REGISTRY.map(p => [p.path, p.type]),
);

// Treat '', undefined, and 'transparent' as equivalent empty color values;
// also compare hex colors case-insensitively.
function colorsMatch(a, b) {
  const empty = v => v === '' || v === undefined || v === 'transparent';
  if (empty(a) && empty(b)) return true;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase() === b.toLowerCase();
  }
  return a === b;
}

// Compare values with numeric coercion: select fields save strings (e.g. "80")
// but defaults/theme values may be numbers (e.g. 80).
function valuesMatch(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b && a !== '' && b !== '' && !isNaN(Number(a)) && !isNaN(Number(b))) {
    return Number(a) === Number(b);
  }
  return false;
}

// Section checks ask "every registry key under this prefix" for every section on
// every rebuild of the design form, and scanning the whole registry with a string
// test per key each time was most of a rebuild. The answer per prefix is kept per
// registry and thrown away when the registry gains a key: font-group fields
// register themselves mid-build (see buildFormSection), so early builds grow it.
// The key count is checked once per builder, not per lookup, so a builder that
// adds keys must call refreshRegistryIndex itself.
const indexByRegistry = new WeakMap();

function getRegistryIndex(registry) {
  const count = Object.keys(registry).length;
  let index = indexByRegistry.get(registry);
  if (!index || index.count !== count) {
    index = { count, byPrefix: new Map() };
    indexByRegistry.set(registry, index);
  }
  return index;
}

function getNestedRegistryKeys(registry, index, key) {
  let keys = index.byPrefix.get(key);
  if (!keys) {
    keys = Object.keys(registry).filter(k => k === key || k.startsWith(key + '.'));
    index.byPrefix.set(key, keys);
  }
  return keys;
}

export class ThemeFieldBuilder {
  constructor(themeValues, setFieldValue, values, seller, page, product, previewColorMode) {
    this.themeValues = themeValues;
    this.setFieldValue = setFieldValue;
    this.values = values;
    this.seller = seller;
    this.page = page;
    this.product = product;
    this.previewColorMode = previewColorMode;
    this.registry = THEME_FIELD_REGISTRY;
    this.refreshRegistryIndex();
  }

  refreshRegistryIndex() {
    this.registryIndex = getRegistryIndex(this.registry);
  }

  nestedRegistryKeys(keys) {
    return keys.flatMap(key => getNestedRegistryKeys(this.registry, this.registryIndex, key));
  }

  /**
   * Determines whether a section's element is present on the current page/product.
   * Used for the "Not on page" badge. Separate from shouldBeOpen (collapse state).
   */
  isOnPage(field) {
    if (!field.isOnPage) return true;
    return !!field.isOnPage(this.fieldContext());
  }

  fieldContext() {
    return {
      page: this.page,
      values: this.values,
      product: this.product,
      seller: this.seller,
    };
  }

  /**
   * Hard gate, unlike isOnPage: an unavailable entry is dropped from the form
   * rather than badged "Not on page". Feature-flagged sections use this so a
   * seller without the flag never sees the section exists.
   */
  isAvailable(field) {
    if (!field?.isAvailable) return true;
    return !!field.isAvailable(this.fieldContext());
  }

  /**
   * Determines whether a collapsible section should default to open.
   * If shouldBeOpen is provided, it's definitive — used by mutually exclusive
   * sub-sections (e.g., layout/logo variants) to stay closed even when their
   * fields have customizations. Otherwise falls back to opening when the
   * section has changes from the base theme.
   */
  shouldCollapsibleBeOpen(field) {
    if (field.shouldBeOpen) {
      return !!field.shouldBeOpen({
        page: this.page,
        values: this.values,
        product: this.product,
      });
    }

    const sectionKeys = this.collectFieldKeysFromStructure(field.fields);
    if (sectionKeys.length > 0 && !this.isSectionAtThemeValue(sectionKeys)) return true;

    return false;
  }

  /**
   * Collects all registry field keys from a field structure array (recursively).
   */
  collectFieldKeysFromStructure(fields) {
    if (!fields) return [];
    const keys = [];
    for (const child of fields) {
      if (typeof child === 'string') {
        keys.push(child);
      } else if (child?.fields) {
        keys.push(...this.collectFieldKeysFromStructure(child.fields));
      } else if (child?.children) {
        keys.push(...this.collectFieldKeysFromStructure(child.children));
      } else if (child?.colorField) {
        keys.push(child.colorField);
      } else if (child?.base) {
        // font-group: collect generated font keys
        keys.push(
          `${child.base}.fontFamily`,
          `${child.base}.fontStyle`,
          `${child.base}.fontWeight`,
          `${child.base}.fontSize`,
        );
      }
    }
    return keys;
  }

  buildField(field, overrides = {}) {
    const registryField = this.registry[field];

    if (!registryField) {
      console.error(`Field ${field} not found in registry`);
      return null;
    }

    const defaultConfig = THEME_FIELD_DEFAULTS[registryField.type];

    const fieldConfig = {
      ...registryField,
      ...defaultConfig,
      isDesignForm: true,
    };

    if (!fieldConfig) {
      console.warn(`Field ${field} not found in registry`);
      return null;
    }

    fieldConfig.id = field;
    fieldConfig.name = field;

    if (overrides.label) fieldConfig.label = overrides.label;

    // A select whose choices depend on another field's value: the structure
    // hands over a function of the registry's options and the form's values.
    if (typeof overrides.options === 'function') {
      fieldConfig.options = overrides.options(fieldConfig.options, { values: this.values });
    }

    if (fieldConfig.type === THEME_FIELD_TYPES.FONT_COMBOBOX && fieldConfig.parentFontField) {
      // Title/Text font show "Auto" when unset; the parent font name is not shown
    }

    if (fieldConfig.type === THEME_FIELD_TYPES.FONT_WEIGHT_SELECT) {
      if (!fieldConfig.fontFamilyFieldName) {
        fieldConfig.fontFamilyFieldName = field
          .replace('.fontWeight', '.fontFamily')
          .replace('.weight', '.family');
      }
    }

    // Color fields render their own inline labels
    if (fieldConfig.type === THEME_FIELD_TYPES.COLOR_DUAL) {
      fieldConfig.hideLabel = true;
    }

    if (fieldConfig.type === THEME_FIELD_TYPES.COLOR_DUAL) {
      fieldConfig.defaultColors = getDefaultColorForPath(
        field,
        this.values,
        this.seller?.brandColor,
        this.page?.hostedLayoutType,
      );

      // set color mode — determines which swatch the color picker highlights
      const colorMode = this.values?.variables?.tokens?.colors;
      if (colorMode === 'dark') {
        fieldConfig.currentColorMode = 'dark';
      } else if (colorMode === 'system') {
        fieldConfig.currentColorMode = this.previewColorMode === 'dark' ? 'dark' : 'light';
      } else {
        // 'light' or unset (undefined/null — default is 'light')
        fieldConfig.currentColorMode = 'light';
      }
    }

    if (fieldConfig.resetable) {
      fieldConfig.canReset = true;
      if (overrides.toggleKey) {
        const resetKeys = [field, overrides.toggleKey];
        fieldConfig.reset = () => this.resetFieldSection(resetKeys);
        fieldConfig.resetToDefaults = () =>
          this.resetFieldSection(resetKeys, { forceToDefaults: true });
        fieldConfig.resetTier = this.isSectionAtThemeValue(resetKeys) ? 2 : 1;
        fieldConfig.isAtDefault = this.isSectionAtDefaultValue(resetKeys);
      } else {
        fieldConfig.reset = () => this.resetField(field);
        fieldConfig.resetTier = this.isAtThemeValue(field) ? 2 : 1;
        fieldConfig.isAtDefault = this.isAtDefaultValue(field);
      }
    }

    if (overrides.toggleKey) {
      fieldConfig.canToggle = true;
      fieldConfig.toggle = () => this.toggleFieldSection(overrides.toggleKey);
      fieldConfig.enabled = this.isFieldEnabled(overrides.toggleKey);
    } else if (fieldConfig.toggleable) {
      fieldConfig.canToggle = true;
      fieldConfig.toggle = () => this.toggleFieldSection(field);
      fieldConfig.enabled = this.isFieldEnabled(field);
    } else {
      fieldConfig.enabled = this.isFieldEnabled(field);
    }

    return fieldConfig;
  }

  buildHeading(key, field) {
    const resetKeys = field.resetKeys || [key];
    return {
      ...field,
      type: 'heading',
      hideLabel: true,
      label: field.label,
      description: field.description,
      level: field.level,
      id: field.id,
      canReset: field.resetable,
      canToggle: field.toggleable,
      enabled: this.isFieldEnabled(field.toggleKey || key),
      reset: () => this.resetFieldSection(resetKeys),
      resetToDefaults: () => this.resetFieldSection(resetKeys, { forceToDefaults: true }),
      resetTier: this.isSectionAtThemeValue(resetKeys) ? 2 : 1,
      isAtDefault: this.isSectionAtDefaultValue(resetKeys),
      toggle: () => this.toggleFieldSection(field.toggleKey || key),
    };
  }

  buildConditionalWrapper(key, field) {
    return {
      ...field,
      type: 'conditional-wrapper',
      enabled: this.isFieldEnabled(field.toggleKey || key),
      children: this.mergeColorAndFontGroups(
        field.children.map(child => this.buildFormSection(key, child)).filter(Boolean),
      ),
    };
  }

  /**
   * Build a part-row config from a declarative `element` entry.
   * Bins the element's bound paths into eyebrows, detects the visual variant,
   * and aggregates reset/toggle controls across all bound paths.
   *
   * Element entry shape:
   *   { type: 'element', id, label, fields: [paths], fontGroupBase?, toggleKey?, resetKeys?, showEye?, resetable?, fieldEyebrows? }
   */
  // The two branding badges (Powered by Stripe / Powered by Checkout Page) are
  // mutually exclusive and plan-gated. Their displayed on/off state is the resolved
  // badge (so they reflect the plan override + precedence), and only paying sellers
  // (active/legacy) may change them — free/trial see them locked. Returns the
  // toggle props to merge into the built field, or null for non-branding fields.
  brandingBadgeBehavior(field) {
    if (!field?.brandingBadge) return null;

    const theme = { elements: this.getNestedValue(this.values, 'variables.elements') };
    const enabled = resolveBadge(this.seller, theme) === field.brandingBadge;

    return {
      enabled,
      // Only paying sellers (active/legacy) may change the branding badges; free/trial
      // see them locked behind the upgrade overlay (CanUseFeature in the renderer).
      locked: !isPayingSeller(this.seller),
      toggle: () => {
        if (enabled) {
          // Turn this badge off (explicit per-page value); the sibling is untouched.
          this.setFieldValue(field.toggleKey, false);
        } else {
          // Turn this badge on and force the mutually-exclusive sibling off.
          this.setFieldValue(field.toggleKey, true);
          if (field.mutuallyExclusiveWith) {
            this.setFieldValue(field.mutuallyExclusiveWith, false);
          }
        }
      },
    };
  }

  buildElementRow(field) {
    const badgeBehavior = this.brandingBadgeBehavior(field);
    const paths = field.fields || [];
    const fontGroupBase = field.fontGroupBase;
    const getType = path => PROPERTY_TYPE_BY_PATH[path];

    // Variant detection includes the synthetic font-group paths.
    const variantPaths = [...paths];
    if (fontGroupBase) {
      variantPaths.push(
        `${fontGroupBase}.fontFamily`,
        `${fontGroupBase}.fontStyle`,
        `${fontGroupBase}.fontWeight`,
        `${fontGroupBase}.fontSize`,
      );
    }
    const variant = field.variant || detectElementVariant(variantPaths, getType);

    // Badge variant: skip eyebrow binning + primary swatch. Expose the two color paths as
    // badgeTextField / badgeBackgroundField (matched by case-insensitive "Background" in path tail).
    if (variant === 'badge') {
      const builtFields = {};
      paths.forEach(p => {
        builtFields[p] = this.buildField(p, {
          label: field.fieldLabels?.[p],
          toggleKey: field.fieldToggleKeys?.[p],
        });
      });

      const isBgPath = p => /Background/i.test(p.slice(p.lastIndexOf('.') + 1));
      const bgPath = paths.find(isBgPath);
      const textPath = paths.find(p => p !== bgPath);
      const badgeBackgroundField = bgPath ? builtFields[bgPath] : null;
      const badgeTextField = textPath ? builtFields[textPath] : null;

      const allBoundPaths = fontGroupBase ? [...paths, fontGroupBase] : paths;
      const resetable = field.resetable !== false && allBoundPaths.length > 0;
      const toggleable = !!field.toggleKey;
      const resetKeys = field.resetKeys || allBoundPaths;

      return {
        type: 'part-row',
        id: field.id,
        label: field.label,
        hideLabel: true,
        variant,
        showEyebrows: false,
        primaryColorField: null,
        eyebrows: [],
        badgeTextField,
        badgeBackgroundField,
        builtFields,
        paths: allBoundPaths,
        isDesignForm: true,
        enabled: badgeBehavior
          ? badgeBehavior.enabled
          : toggleable
            ? this.isFieldEnabled(field.toggleKey)
            : true,
        canReset: resetable,
        canToggle: toggleable,
        reset: resetable ? () => this.resetFieldSection(resetKeys) : undefined,
        resetToDefaults: resetable
          ? () => this.resetFieldSection(resetKeys, { forceToDefaults: true })
          : undefined,
        resetTier: resetable && this.isSectionAtThemeValue(resetKeys) ? 2 : 1,
        isAtDefault: resetable ? this.isSectionAtDefaultValue(resetKeys) : true,
        toggle: toggleable ? () => this.toggleFieldSection(field.toggleKey) : undefined,
        showEye: field.showEye !== false && toggleable,
      };
    }

    // Bin only the explicit (non-font-group) paths. `fieldEyebrows` lets the
    // element re-home specific paths (path → eyebrow id), e.g. into Marker.
    const allEyebrows = binFieldsIntoEyebrows(paths, getType, field.fieldEyebrows);

    const builtFields = {};
    paths.forEach(p => {
      builtFields[p] = this.buildField(p, {
        label: field.fieldLabels?.[p],
        toggleKey: field.fieldToggleKeys?.[p],
      });
    });

    // Pick the primary swatch field — prefer text-paint (color, textColor, …) over surface paint.
    // Set for any variant where the row should display a single representative color in its header.
    const fillEb = allEyebrows.find(e => e.id === 'fill');
    let primaryPath = null;
    if (fillEb && fillEb.fields.length > 0 && field.hideSwatch !== true) {
      const lastSeg = p => p.slice(p.lastIndexOf('.') + 1);
      primaryPath = fillEb.fields.find(p => isTextPaintTail(lastSeg(p))) || fillEb.fields[0];
    }
    const primaryColorField = primaryPath ? builtFields[primaryPath] : null;

    // Build eyebrows, dropping the primary path from Fill (it's shown in the row header).
    let eyebrows = allEyebrows
      .map(eb => ({
        type: 'eyebrow',
        id: eb.id,
        title: eb.title,
        fields: eb.fields
          .filter(p => p !== primaryPath)
          .map(p => builtFields[p])
          .filter(Boolean),
      }))
      .filter(eb => eb.fields.length > 0);

    // Inject font-group as a Typography eyebrow entry if specified. Inside an
    // element row's Typography eyebrow, the design pairs Font/Style and
    // Weight/Size into 2×2 columns rather than a single 4-col row.
    if (fontGroupBase) {
      const fontGroupField = this.buildFormSection(null, {
        type: 'font-group',
        base: fontGroupBase,
        columns: 2,
      });
      let typoEb = eyebrows.find(eb => eb.id === 'typography');
      if (!typoEb) {
        typoEb = { type: 'eyebrow', id: 'typography', title: 'Typography', fields: [] };
        const insertAt = eyebrows.findIndex(eb => eb.id === 'layout');
        if (insertAt === -1) eyebrows.push(typoEb);
        else eyebrows.splice(insertAt, 0, typoEb);
      }
      typoEb.fields.push(fontGroupField);
    }

    const displayEyebrows = eyebrows;

    // Single-setting rule: if exactly one field remains, no eyebrow header.
    // Exception: rows with a fontGroupBase always show eyebrows so the
    // TYPOGRAPHY label appears above the Font/Style/Weight/Size grid even when
    // the font-group is the only remaining field after primary-swatch hoist.
    const totalFields = displayEyebrows.reduce((n, eb) => n + eb.fields.length, 0);
    const showEyebrows = displayEyebrows.length > 0 && (totalFields > 1 || !!fontGroupBase);

    // Aggregated reset/toggle across all bound paths (incl. font-group base).
    const allBoundPaths = fontGroupBase ? [...paths, fontGroupBase] : paths;
    const resetable = field.resetable !== false && allBoundPaths.length > 0;
    const toggleable = !!field.toggleKey;
    const resetKeys = field.resetKeys || allBoundPaths;

    return {
      type: 'part-row',
      id: field.id,
      label: field.label,
      hideLabel: true,
      variant,
      showEyebrows,
      primaryColorField,
      eyebrows: displayEyebrows,
      builtFields,
      paths: allBoundPaths,
      isDesignForm: true,
      enabled: badgeBehavior
        ? badgeBehavior.enabled
        : toggleable
          ? this.isFieldEnabled(field.toggleKey)
          : true,
      canReset: resetable,
      canToggle: toggleable,
      reset: resetable ? () => this.resetFieldSection(resetKeys) : undefined,
      resetToDefaults: resetable
        ? () => this.resetFieldSection(resetKeys, { forceToDefaults: true })
        : undefined,
      resetTier: resetable && this.isSectionAtThemeValue(resetKeys) ? 2 : 1,
      isAtDefault: resetable ? this.isSectionAtDefaultValue(resetKeys) : true,
      toggle: badgeBehavior
        ? badgeBehavior.toggle
        : toggleable
          ? () => this.toggleFieldSection(field.toggleKey)
          : undefined,
      locked: badgeBehavior?.locked || false,
      lockMessage: field.lockMessage,
      showEye: field.showEye !== false && toggleable,
    };
  }

  buildLabelToggle(key, field) {
    const resetKeys = field.resetKeys || [key];
    return {
      ...field,
      type: 'label-toggle',
      hideLabel: true,
      label: field.label,
      id: field.id,
      toggleKey: field.toggleKey || key,
      enabled: this.isFieldEnabled(field.toggleKey || key),
      canToggle: !!field.toggleKey,
      canReset: !!field.resetKeys,
      toggle: () => this.toggleFieldSection(field.toggleKey || key),
      reset: () => this.resetFieldSection(resetKeys),
      resetToDefaults: () => this.resetFieldSection(resetKeys, { forceToDefaults: true }),
      resetTier: this.isSectionAtThemeValue(resetKeys) ? 2 : 1,
      isAtDefault: this.isSectionAtDefaultValue(resetKeys),
    };
  }

  buildFormSection(key, field) {
    if (typeof field === 'string') {
      return this.buildField(field);
    }

    if (!this.isAvailable(field)) return null;

    if (field?.type === 'fieldgroup') {
      const groupId = field.id || key;
      const result = {
        type: 'fieldgroup',
        id: groupId,
        divider: field.divider,
        className: field.className,
        isDesignForm: true,
        fields: this.mergeColorAndFontGroups(
          field.fields.map(child => this.buildFormSection(key, child)).filter(Boolean),
        ),
      };
      if (field.collapsible) {
        result.collapsible = true;
        result.defaultOpen = this.shouldCollapsibleBeOpen(field);
        result.isRelevant = this.isOnPage(field);
        // When shouldBeOpen explicitly returns false, allow auto-closing
        // (e.g., switching between mutually exclusive layout/logo sub-sections)
        if (field.shouldBeOpen) {
          result.autoClose = !field.shouldBeOpen({
            page: this.page,
            values: this.values,
            product: this.product,
          });
        }
      }
      return result;
    }

    if (field?.type === 'columns') {
      return {
        type: 'columns',
        columns: field.columns,
        gap: 'sm',
        isDesignForm: true,
        wrapperClassName: field.wrapperClassName,
        fields: field.fields.map(f => {
          if (typeof f === 'string') return this.buildField(f);
          if (f?.path) return this.buildField(f.path, f);
          return f;
        }),
      };
    }

    if (field?.type === 'subheader') {
      return {
        type: 'subheader',
        id: field.id,
        label: field.label,
        divider: field.divider,
        hideLabel: true,
        isDesignForm: true,
      };
    }

    if (field?.type === 'subgroup') {
      return {
        type: 'subgroup',
        id: field.id,
        label: field.label,
        hideLabel: true,
        isDesignForm: true,
      };
    }

    if (field?.type === 'preset-picker') {
      return {
        type: 'preset-picker',
        id: field.id,
        hideLabel: true,
        isDesignForm: true,
      };
    }

    if (field?.type === 'caption') {
      return {
        type: 'caption',
        id: field.id,
        text: field.text,
        // Optional inline link (e.g. "…in the Product tab") and trailing text,
        // resolved to a route by the caption renderer.
        link: field.link,
        textAfter: field.textAfter,
        hideLabel: true,
        isDesignForm: true,
      };
    }

    if (field?.type === 'element') {
      return this.buildElementRow(field);
    }

    if (field?.type === 'font-group') {
      const base = field.base;
      const columns = field.columns || 4;
      const labels = field.labels || {
        family: 'Font',
        style: 'Style',
        weight: 'Weight',
        size: 'Size',
      };

      const familyField = {
        id: `${base}.fontFamily`,
        name: `${base}.fontFamily`,
        type: THEME_FIELD_TYPES.FONT_COMBOBOX,
        label: labels.family,
        resetable: true,
        enabled: this.isFieldEnabled(base),
        isDesignForm: true,
      };

      const styleField = {
        ...THEME_FIELD_DEFAULTS[THEME_FIELD_TYPES.SELECT],
        id: `${base}.fontStyle`,
        name: `${base}.fontStyle`,
        type: THEME_FIELD_TYPES.SELECT,
        label: labels.style,
        options: FONT_STYLE_OPTIONS,
        enabled: this.isFieldEnabled(base),
        isDesignForm: true,
      };

      const weightField = {
        ...THEME_FIELD_DEFAULTS[THEME_FIELD_TYPES.FONT_WEIGHT_SELECT],
        id: `${base}.fontWeight`,
        name: `${base}.fontWeight`,
        type: THEME_FIELD_TYPES.FONT_WEIGHT_SELECT,
        label: labels.weight,
        fontFamilyFieldName: `${base}.fontFamily`,
        enabled: this.isFieldEnabled(base),
        isDesignForm: true,
      };

      const sizeField = {
        ...THEME_FIELD_DEFAULTS[THEME_FIELD_TYPES.SELECT],
        id: `${base}.fontSize`,
        name: `${base}.fontSize`,
        type: THEME_FIELD_TYPES.SELECT,
        label: labels.size,
        options: FONT_SIZE_OPTIONS,
        enabled: this.isFieldEnabled(base),
        isDesignForm: true,
      };

      // Register generated fields for reset/toggle discovery
      this.registry[familyField.name] = {
        type: THEME_FIELD_TYPES.FONT_COMBOBOX,
        label: labels.family,
        resetable: true,
      };
      this.registry[styleField.name] = {
        type: THEME_FIELD_TYPES.SELECT,
        label: labels.style,
        options: FONT_STYLE_OPTIONS,
      };
      this.registry[weightField.name] = {
        type: THEME_FIELD_TYPES.FONT_WEIGHT_SELECT,
        label: labels.weight,
      };
      this.registry[sizeField.name] = {
        type: THEME_FIELD_TYPES.SELECT,
        label: labels.size,
        options: FONT_SIZE_OPTIONS,
      };
      this.refreshRegistryIndex();

      const fontKeys = [
        `${base}.fontFamily`,
        `${base}.fontStyle`,
        `${base}.fontWeight`,
        `${base}.fontSize`,
      ];
      const fontGroupHasChanges = !this.isSectionAtDefaultValue(fontKeys);

      return {
        type: 'font-group-columns',
        hideLabel: true,
        name: `font-group-${base}`,
        columns,
        gap: 'sm',
        isDesignForm: true,
        fontGroupHasChanges,
        fields: [familyField, styleField, weightField, sizeField],
      };
    }

    if (field?.type === 'heading') {
      return this.buildHeading(key, field);
    }

    if (field?.type === 'conditional-wrapper') {
      return this.buildConditionalWrapper(key, field);
    }

    if (field?.type === 'label-toggle') {
      return this.buildLabelToggle(key, field);
    }

    if (field?.type === 'group-color') {
      const base = field.base;
      const colorFieldName = field.colorField;
      const colorField = this.buildField(colorFieldName);

      // Allow custom toggleKey, otherwise default to ${base}.enabled
      const toggleKey = field.toggleKey || `${base}.enabled`;

      // Disable single-field reset and attach group controls
      // Use groupResetKeys if provided, otherwise reset all fields under base path
      const resetKeys = field.groupResetKeys || [base];
      const enhancedColorField = {
        ...colorField,
        canReset: false,
        groupCanToggle: field.groupCanToggle !== false,
        groupCanReset: field.groupCanReset !== false,
        groupToggleKey: toggleKey,
        groupEnabled: this.isFieldEnabled(toggleKey),
        groupToggle: () => this.toggleFieldSection(toggleKey),
        groupReset: () => this.resetFieldSection(resetKeys),
        groupResetToDefaults: () => this.resetFieldSection(resetKeys, { forceToDefaults: true }),
        groupResetTier: this.isSectionAtThemeValue(resetKeys) ? 2 : 1,
        groupIsAtDefault: this.isSectionAtDefaultValue(resetKeys),
      };

      return enhancedColorField;
    }

    return {};
  }

  /**
   * Merges adjacent color-select-dual + font-group-columns into a single
   * compound field so the chevron disclosure can be rendered inline.
   */
  mergeColorAndFontGroups(fields) {
    const result = [];
    for (let i = 0; i < fields.length; i++) {
      const current = fields[i];
      const next = fields[i + 1];

      // Direct: color-select-dual followed by font-group-columns
      if (current?.type === 'color-select-dual' && next?.type === 'font-group-columns') {
        result.push({
          ...current,
          type: 'color-group-with-fonts',
          fontGroupFields: next.fields,
          fontGroupColumns: next.columns,
          fontGroupHasChanges: next.fontGroupHasChanges,
        });
        i++;
        continue;
      }

      // Wrapped: color-select-dual followed by conditional-wrapper whose
      // first child is a font-group-columns
      if (
        current?.type === 'color-select-dual' &&
        next?.type === 'conditional-wrapper' &&
        next.children?.[0]?.type === 'font-group-columns'
      ) {
        const fontGroup = next.children[0];
        const remainingChildren = next.children.slice(1);
        result.push({
          ...current,
          type: 'color-group-with-fonts',
          fontGroupFields: fontGroup.fields,
          fontGroupColumns: fontGroup.columns,
          fontGroupHasChanges: fontGroup.fontGroupHasChanges,
          fontGroupEnabled: next.enabled,
        });
        // If the conditional-wrapper had more children, keep them
        if (remainingChildren.length > 0) {
          result.push({ ...next, children: remainingChildren });
        } else {
          i++;
        }
        continue;
      }

      result.push(current);
    }
    return result;
  }

  /**
   * Checks whether the field is enabled
   * If the field is a nested field, it will check the closest parent
   * @param {string} field - The field to check
   * @returns {boolean} - Whether the field is enabled
   */
  isFieldEnabled(field) {
    if (field.endsWith('.enabled')) {
      return this.getNestedValue(this.values, field) !== false;
    } else {
      return this.getNestedValue(this.values, `${field}.enabled`) !== false;
    }
  }

  /**
   * Reset all fields in a section
   * If a key is a prefix, all fields that start with that key will be reset
   * e.g. 'variables.tokens' will reset all fields that start with 'variables.tokens', including 'variables.tokens.colorPrimary'
   * Also automatically enables all nested sections with .enabled flags
   * @param {string[]} keys - The keys of the fields to reset
   * @returns {void}
   */
  resetFieldSection(keys, { forceToDefaults = false } = {}) {
    this.refreshRegistryIndex();
    // Find all nested field keys (including exact matches)
    const nestedKeys = this.nestedRegistryKeys(keys);

    // Find all unique paths that could have .enabled flags
    // This includes the parent keys and all intermediate paths
    const allPaths = new Set();

    // Add the original keys
    keys.forEach(key => allPaths.add(key));

    // Add all nested field paths and their parent paths
    nestedKeys.forEach(key => {
      const parts = key.split('.');
      // Build all intermediate paths (e.g., 'a.b.c' -> ['a', 'a.b', 'a.b.c'])
      for (let i = 1; i <= parts.length; i++) {
        allPaths.add(parts.slice(0, i).join('.'));
      }
    });

    // Reset .enabled flags to their theme values (default to true if not in theme)
    allPaths.forEach(path => {
      if (this.getNestedValue(this.values, `${path}.enabled`) !== undefined) {
        const themeEnabled = this.getNestedValue(this.themeValues, `${path}.enabled`);
        this.setFieldValue(`${path}.enabled`, themeEnabled !== false);
      }
    });

    // Reset all nested field values
    nestedKeys.forEach(key => {
      if (forceToDefaults) {
        this.clearFieldToDefault(key);
      } else {
        const themeValue = this.getNestedValue(this.themeValues, key);
        if (themeValue !== undefined && themeValue !== null) {
          this.setFieldValue(key, themeValue);
        } else {
          this.clearFieldToDefault(key);
        }
      }
    });
  }

  toggleFieldSection(key) {
    if (key.endsWith('.enabled')) {
      this.setFieldValue(key, !this.isKeyEnabled(key));
    } else {
      this.setFieldValue(`${key}.enabled`, !this.isKeyEnabled(`${key}.enabled`));
    }
  }

  isKeyEnabled(key) {
    const value = this.getNestedValue(this.values, key);

    // When key points directly to a boolean (e.g., *.enabled), treat value as boolean
    if (typeof value === 'boolean' || value === undefined || value === null) {
      return value !== false;
    }

    // When key points to an object, read its enabled flag
    return value?.enabled !== false;
  }

  getNestedValue(obj, path) {
    if (!obj) return undefined;
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  getDefaultValue(field) {
    const fieldConfig = this.registry[field];
    if (!fieldConfig) {
      console.warn(`Field ${field} not found in registry`);
      return null;
    }

    return fieldConfig.defaultValue;
  }

  isAtThemeValue(field) {
    const currentValue = this.getNestedValue(this.values, field);
    const themeValue = this.getNestedValue(this.themeValues, field);

    // Color objects: compare light/dark
    if (
      currentValue &&
      typeof currentValue === 'object' &&
      (currentValue.light !== undefined || currentValue.dark !== undefined)
    ) {
      const tv = themeValue || { light: '', dark: '' };
      return currentValue.light === tv.light && currentValue.dark === tv.dark;
    }

    return valuesMatch(currentValue, themeValue);
  }

  isSectionAtThemeValue(keys) {
    const nestedKeys = this.nestedRegistryKeys(keys);
    return nestedKeys.every(key => this.isAtThemeValue(key));
  }

  /**
   * Returns what clearFieldToDefault would set for this field, without actually setting it.
   */
  getSystemDefault(field) {
    // 1. Check full variable defaults (BASE_VARIABLES_DEFAULTS + element/layout color defaults)
    const path = field.startsWith('variables.') ? field.slice('variables.'.length) : field;
    const fullDefault = this.getNestedValue(getFullVariableDefaults(), path);
    if (fullDefault !== undefined && fullDefault !== null) return fullDefault;

    // 2. For token color fields, compute system defaults directly (avoid circular reference)
    if (path.startsWith('tokens.')) {
      const tokenName = path.split('.')[1];
      const brandColor = this.seller?.brandColor;
      const TOKEN_COLOR_DEFAULTS = {
        colorPrimary: { light: brandColor || '#231c1e', dark: brandColor || '#231c1e' },
        colorSecondary: { light: brandColor || '#231c1e', dark: brandColor || '#231c1e' },
        textPrimary: { light: '#231c1e', dark: '#231c1e' },
        textSecondary: { light: '#7f6d72', dark: '#7f6d72' },
        textTertiary: { light: '#b0a3a8', dark: '#b0a3a8' },
      };
      if (TOKEN_COLOR_DEFAULTS[tokenName]) return TOKEN_COLOR_DEFAULTS[tokenName];
    }

    // 3. For color fields, try getDefaultColorForPath (resolves inheritance from tokens).
    //    Only call for actual color fields — the resolver can return spurious
    //    color objects for non-color paths via inheritance.
    if (this.registry[field]?.type === THEME_FIELD_TYPES.COLOR_DUAL) {
      const colorDefault = getDefaultColorForPath(
        field,
        this.values,
        this.seller?.brandColor,
        this.page?.hostedLayoutType,
      );
      if (colorDefault && (colorDefault.light || colorDefault.dark)) return colorDefault;
    }

    // 4. Fall back to registry defaultValue
    const registryDefault = this.getDefaultValue(field);
    if (registryDefault !== undefined && registryDefault !== null) return registryDefault;

    // 5. Heuristic fallbacks for fields not in BASE_VARIABLES_DEFAULTS or registry
    if (
      field.includes('color') ||
      field.includes('Color') ||
      field.includes('textPrimary') ||
      field.includes('textSecondary') ||
      field.includes('textTertiary')
    ) {
      return { light: '', dark: '' };
    }
    if (field.includes('fontFamily') || field.includes('.family') || field.includes('fontStyle')) {
      return '';
    }
    if (
      field.includes('fontSize') ||
      field.includes('.size') ||
      field.includes('fontWeight') ||
      field.includes('.weight')
    ) {
      return 0;
    }
    if (field.includes('backgroundPosition')) return 'center';
    if (field.includes('backgroundWidth')) return 'half';
    if (field.includes('backgroundImage')) return null;
    if (field.includes('borderWidth')) return null;
    return '';
  }

  /**
   * Checks whether a field's current value already matches the system default.
   */
  isAtDefaultValue(field) {
    const currentValue = this.getNestedValue(this.values, field);
    const defaultValue = this.getSystemDefault(field);

    // If the value is not set, there's nothing to reset — treat as at default
    if (currentValue === undefined || currentValue === null) return true;

    // Color objects
    if (
      typeof currentValue === 'object' &&
      (currentValue.light !== undefined || currentValue.dark !== undefined)
    ) {
      const dv = defaultValue || { light: '', dark: '' };
      // Empty string means "no value set, use default" — always treat as at-default
      const lightAtDefault =
        currentValue.light === '' || currentValue.light === undefined
          ? true
          : colorsMatch(currentValue.light, dv.light);
      const darkAtDefault =
        currentValue.dark === '' || currentValue.dark === undefined
          ? true
          : colorsMatch(currentValue.dark, dv.dark);
      return lightAtDefault && darkAtDefault;
    }

    return valuesMatch(currentValue, defaultValue);
  }

  isSectionAtDefaultValue(keys) {
    const nestedKeys = this.nestedRegistryKeys(keys);
    return nestedKeys.every(key => this.isAtDefaultValue(key));
  }

  clearFieldToDefault(field) {
    const systemDefault = this.getSystemDefault(field);

    // Color fields: clear to empty so the inheritance chain resolves at render time
    // (e.g., Total color inherits from the current textSecondary token, not a static snapshot)
    if (
      typeof systemDefault === 'object' &&
      systemDefault !== null &&
      (systemDefault.light !== undefined || systemDefault.dark !== undefined)
    ) {
      this.setFieldValue(field, { light: '', dark: '' });
      return;
    }

    if (systemDefault !== undefined && systemDefault !== null) {
      this.setFieldValue(field, systemDefault);
    } else {
      this.setFieldValue(field, '');
    }
  }

  resetField(field) {
    if (!this.isAtThemeValue(field)) {
      // Tier 1: reset to theme value
      const themeValue = this.getNestedValue(this.themeValues, field);
      if (themeValue !== undefined && themeValue !== null) {
        this.setFieldValue(field, themeValue);
        return;
      }
    }
    // Tier 2: clear to system default
    this.clearFieldToDefault(field);
  }
}
